// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Assume the reader knows nothing about fuel markets. Every term used anywhere
// in the interface that is not plain English lives here, and is surfaced by an
// inline ⓘ link.

export interface Term { term: string; definition: string }

export const GLOSSARY: Record<string, Term> = {
  cycle: {
    term: 'Price cycle',
    definition: 'Petrol prices in most Australian cities do not drift — they saw-tooth. Prices jump sharply on one day (the "hike"), then drift down a little each day until the next hike. The cycle is not driven by the wholesale oil price; it is a pricing strategy retailers move in step on. Perth\'s cycle is exactly seven days long by law. Brisbane\'s is not.',
  },
  amplitude: {
    term: 'Amplitude',
    definition: 'The size of the cycle: the gap between the cheapest and the dearest day, in cents per litre. In Perth this has ranged from under 1c (2008, when there was effectively no cycle) to over 45c (2024).',
  },
  detrended: {
    term: 'Detrended',
    definition: 'Each week\'s prices measured against that same week\'s own average, rather than against the whole month or year. This removes the underlying rise or fall in the oil price so what is left is the cycle alone. Without it, a month in which prices simply fell would appear to have a "cheap Saturday" that does not exist.',
  },
  median: {
    term: 'Median',
    definition: 'The middle value: half the stations were cheaper, half dearer. Used here rather than the average because a handful of remote roadhouses charging 400c would drag an average badly.',
  },
  ulp: {
    term: 'Unleaded 91',
    definition: 'Standard unleaded petrol, the cheapest grade at most stations. It is the only fuel present in every one of the 25 years of data, so all long-run comparisons on this site use it.',
  },
  region: {
    term: 'Region',
    definition: 'In Western Australia, FuelWatch\'s own ten regions (Metro, Peel, Pilbara and so on), which have been stable since 2001. In Queensland, the Australian Bureau of Statistics\' Statistical Area Level 4 — the region each station falls inside, worked out from its published coordinates.',
  },
  sa4: {
    term: 'Statistical Area Level 4',
    definition: 'The ABS\'s standard regional geography — 29 of them cover Western Australia and Queensland. They are the areas labour force statistics are reported for, so they are the natural unit for comparing one part of a state with another.',
  },
  tomorrow: {
    term: 'Tomorrow\'s price',
    definition: 'Western Australia is the only place in Australia where you can know tomorrow\'s price today. Under the Petroleum Products Pricing Act, retailers must notify FuelWatch of tomorrow\'s price by 2pm and then hold that price for a full 24 hours from 6am. Queensland has no such rule.',
  },
  changeLog: {
    term: 'Change log',
    definition: 'Queensland stations report a price only when it changes, so the raw file is a list of changes rather than a daily reading. To compare Queensland with Western Australia, each price has to be carried forward from the day it was set until the day it changed.',
  },
  snapshot: {
    term: 'Daily snapshot',
    definition: 'Western Australia publishes every station\'s price for every product on every day, whether or not it changed — a complete daily photograph of the market. That is what makes a 25-year daily series possible.',
  },
  spread: {
    term: 'Spread',
    definition: 'The gap between the cheapest and the dearest station on the same day in the same area. It is the money on the table for driving a few extra minutes, and it is usually much larger than people expect.',
  },
  premium: {
    term: 'Brand premium',
    definition: 'How much more (or less) a brand charges than the middle of the market in the same region. Comparing brands on raw price alone would just be measuring where their stations happen to be — a brand with many outback sites would look expensive for reasons that have nothing to do with the brand.',
  },
  rebrand: {
    term: 'Rebrand',
    definition: 'A service station changing banner without moving — Caltex sites becoming Ampol, Coles Express becoming Reddy Express. Because the data identifies each station individually over time, these changes are visible as the same site reporting under a new name.',
  },
  trough: {
    term: 'Trough',
    definition: 'The cheapest day of a given week. Reported as "the modal trough" — the day that is cheapest most often — together with how reliably that holds.',
  },
  confidence: {
    term: 'Reliability',
    definition: 'The share of weeks in which the named day really was the cheapest. Seven days means pure chance would give about 14%. Perth in 2024 scored 100%; Brisbane typically scores close to chance, which is why there is no "cheap day" advice for Queensland.',
  },
};

let popover: HTMLDivElement | null = null;

function hide(): void {
  if (popover) {
    popover.remove();
    popover = null;
  }
}

/** `<span class="glossary-link" data-term="cycle">price cycle</span>` markup. */
export function glossaryTerm(key: keyof typeof GLOSSARY | string, label?: string): string {
  const entry = GLOSSARY[key];
  if (!entry) return label ?? String(key);
  return `<span class="glossary-link" data-term="${key}" role="button" tabindex="0" aria-label="What is ${entry.term}?">${label ?? entry.term.toLowerCase()}</span>`;
}

export function initGlossary(): void {
  document.addEventListener('click', (e) => {
    const link = (e.target as Element).closest?.('.glossary-link') as HTMLElement | null;
    if (!link) {
      if (popover && !popover.contains(e.target as Node)) hide();
      return;
    }
    e.preventDefault();
    const key = link.dataset.term ?? '';
    const entry = GLOSSARY[key];
    if (!entry) return;
    hide();
    popover = document.createElement('div');
    popover.className = 'glossary-pop';
    popover.setAttribute('role', 'dialog');
    popover.innerHTML = `<h4></h4><p></p>`;
    popover.querySelector('h4')!.textContent = entry.term;
    popover.querySelector('p')!.textContent = entry.definition;
    document.body.appendChild(popover);
    const r = link.getBoundingClientRect();
    const pr = popover.getBoundingClientRect();
    const left = Math.min(Math.max(8, r.left), window.innerWidth - pr.width - 8);
    const below = r.bottom + 8;
    const top = below + pr.height > window.innerHeight - 8 ? Math.max(8, r.top - pr.height - 8) : below;
    popover.style.left = `${left}px`;
    popover.style.top = `${top}px`;
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hide();
    if ((e.key === 'Enter' || e.key === ' ') && (e.target as Element)?.classList?.contains('glossary-link')) {
      e.preventDefault();
      (e.target as HTMLElement).click();
    }
  });
  window.addEventListener('scroll', hide, { passive: true });
}
